import React, {
	useCallback, useEffect, useRef, useState
} from 'react'
import {
	Calendar,
	Checkbox,
	Popover,
	Select,
	TimePicker,
	Tooltip
} from 'antd'
import { FieldTimeOutlined } from '@ant-design/icons'
import moment from 'moment'

export interface DateValue {
	startAt: moment.Moment | null
	deadlineAt: moment.Moment | null
	recurringPattern: string
	recurringWeekDays: string[]
	reminderOffsetHours: number | null
	completionWindowHours: number | null
	fixedCompletion: boolean
}

export interface LinkedTemplate {
	id: number
	title: string
	frequency: string
	workingDaysOnly: boolean
	weekDays?: string[]
	completionWindowHours?: number | null
}

interface Props {
	value?: DateValue
	onChange?: (v: DateValue) => void
	autoOpen?: boolean
	onDismiss?: () => void
	onRemove?: () => void
	recurrencePill?: { label: string; readOnly?: boolean; inactive?: boolean; onClick?: () => void }
	autoFocusRecurrence?: boolean
	onAutoFocusRecurrenceHandled?: () => void
	linkedTemplate?: LinkedTemplate
	onOpenTemplate?: () => void
}

const DEFAULT_VALUE: DateValue = {
	startAt: null,
	deadlineAt: null,
	recurringPattern: 'NEVER',
	recurringWeekDays: [],
	reminderOffsetHours: null,
	completionWindowHours: null,
	fixedCompletion: false
}

const deriveDeadlineLabel = (base: moment.Moment | null, hours: number | null): string => {
	if (!base || !hours || hours <= 0) return '-'
	return base.clone().add(hours, 'hours').format('D.M.YYYY HH:mm')
}

const WEEK_DAYS = ['Po', 'Ut', 'St', 'Št', 'Pi', 'So', 'Ne']
const DAY_KEYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']

const reminderOptions: { value: number | null; label: string }[] = [
	{ value: null, label: 'Žiadna' },
	{ value: 24, label: '1 deň pred' },
	{ value: 48, label: '2 dni pred' },
	{ value: 168, label: '1 týždeň pred' }
]

const DATE_FORMATS = ['D.M.YYYY', 'DD.MM.YYYY', 'D.M.YY', 'DD.MM.YY']

interface DateInputProps {
	value: moment.Moment | null
	onCommit: (m: moment.Moment) => void
}

const DateInput = ({ value: inputVal, onCommit }: DateInputProps) => {
	const [local, setLocal] = useState(inputVal ? inputVal.format('D.M.YYYY') : '')

	useEffect(() => {
		setLocal(inputVal ? inputVal.format('D.M.YYYY') : '')
	}, [inputVal])

	const commit = () => {
		const parsed = moment(local.trim(), DATE_FORMATS, true)
		if (parsed.isValid()) {
			onCommit(parsed)
		} else {
			setLocal(inputVal ? inputVal.format('D.M.YYYY') : '')
		}
	}

	return (
		<input
			value={local}
			onChange={(e) => setLocal(e.target.value)}
			onBlur={commit}
			onKeyDown={(e) => {
				if (e.key === 'Enter') {
					e.preventDefault()
					commit()
				}
			}}
			placeholder={'D.M.RRRR'}
			style={{
				border: '1px solid #d1d5db',
				borderRadius: 6,
				padding: '3px 8px',
				fontSize: 13,
				width: 92,
				outline: 'none',
				color: '#374151',
				fontFamily: 'inherit'
			}}
		/>
	)
}

const DatePickerPopover = ({
	value,
	onChange,
	autoOpen,
	onDismiss,
	onRemove,
	recurrencePill,
	autoFocusRecurrence,
	onAutoFocusRecurrenceHandled,
	linkedTemplate,
	onOpenTemplate
}: Props) => {
	const [open, setOpen] = useState(() => autoOpen ?? false)
	const [dueError, setDueError] = useState(false)
	const [weekDayError, setWeekDayError] = useState(false)
	const [draft, setDraft] = useState<DateValue>(() => {
		if (autoOpen && !value?.deadlineAt && !value?.startAt) {
			return {
				...DEFAULT_VALUE,
				deadlineAt: moment().add(1, 'day').hour(12).minute(0).second(0)
			}
		}
		return value ?? DEFAULT_VALUE
	})
	const [calendarCurrent, setCalendarCurrent] = useState(() => moment().startOf('month'))
	const recurrenceSectionRef = useRef<HTMLDivElement | null>(null)

	const normalizeWeekDays = (raw: string[] | string | null | undefined): string[] => {
		if (!raw) return []
		if (Array.isArray(raw)) return raw
		try { return JSON.parse(raw as unknown as string) } catch { return [] }
	}

	useEffect(() => {
		if (open) {
			const next = value ?? DEFAULT_VALUE
			setDraft({ ...DEFAULT_VALUE, ...next, recurringWeekDays: normalizeWeekDays(next.recurringWeekDays as string[] | string) })
			setCalendarCurrent((next.deadlineAt ?? next.startAt ?? moment()).clone().startOf('month'))
		}
	}, [open, value])

	useEffect(() => {
		if (autoOpen) {
			setOpen(true)
		}
	}, [autoOpen])

	useEffect(() => {
		if (!open || !autoFocusRecurrence) return
		window.setTimeout(() => {
			const section = recurrenceSectionRef.current
			section?.scrollIntoView({ block: 'center' })
			const select = section?.querySelector('.ant-select-selector') as HTMLElement | null
			select?.focus()
			onAutoFocusRecurrenceHandled?.()
		}, 0)
	}, [autoFocusRecurrence, onAutoFocusRecurrenceHandled, open])

	const startEnabled = draft.startAt !== null
	const dueEnabled = draft.deadlineAt !== null

	const handleCalendarSelect = useCallback((date: moment.Moment) => {
		setDraft((prev) => {
			const sEn = prev.startAt !== null
			const dEn = prev.deadlineAt !== null

			if (sEn && dEn) {
				// Both enabled:
				// - click before start → start shifts to clicked date, due shifts to old start
				// - click on start or after → always moves due
				const start = prev.startAt!
				const due = prev.deadlineAt!
				const dueHour = due.hour()
				const dueMinute = due.minute()

				if (date.isBefore(start, 'day')) {
					return {
						...prev,
						startAt: date.clone().startOf('day'),
						deadlineAt: start.clone().hour(dueHour).minute(dueMinute).second(0)
					}
				}
				return { ...prev, deadlineAt: date.clone().hour(dueHour).minute(dueMinute).second(0) }
			}
			if (sEn) {
				return { ...prev, startAt: date.clone().startOf('day') }
			}
			if (dEn) {
				return { ...prev, deadlineAt: date.clone().hour(12).minute(0).second(0) }
			}
			return prev
		})
	}, [])

	const handleStartAtCheck = useCallback((checked: boolean) => {
		setDraft((prev) => {
			if (!checked) {
				return { ...prev, startAt: null }
			}
			// Prefill: one day before deadlineAt if it exists, else today
			const prefill = prev.deadlineAt
				? prev.deadlineAt.clone().subtract(1, 'day').startOf('day')
				: moment().startOf('day')
			return { ...prev, startAt: prev.startAt ?? prefill }
		})
	}, [])

	const handleDeadlineCheck = useCallback((checked: boolean) => {
		setDraft((prev) => {
			if (!checked) {
				const nextPattern = prev.recurringPattern === 'MONTHLY_DAY' ? 'NEVER' : prev.recurringPattern
				return { ...prev, deadlineAt: null, recurringPattern: nextPattern }
			}
			// Prefill: one day after startAt if it exists, else tomorrow
			const prefill = prev.startAt
				? prev.startAt.clone().add(1, 'day').hour(12).minute(0).second(0)
				: moment().add(1, 'day').hour(12).minute(0).second(0)
			return { ...prev, deadlineAt: prev.deadlineAt ?? prefill }
		})
	}, [])

	const handleTimeChange = useCallback((time: moment.Moment | null) => {
		if (!time) return
		setDraft((prev) => {
			if (!prev.deadlineAt) return prev
			const next = prev.deadlineAt.clone()
			next.hour(time.hour())
			next.minute(time.minute())
			next.second(0)
			return { ...prev, deadlineAt: next }
		})
	}, [])

	const handleRecurringPattern = useCallback((val: string) => {
		setWeekDayError(false)
		setDraft((prev) => ({
			...prev,
			recurringPattern: val,
			fixedCompletion: val === 'NEVER' ? false : prev.fixedCompletion
		}))
	}, [])

	const handleToggleWeekDay = useCallback((key: string) => {
		setWeekDayError(false)
		setDraft((prev) => {
			const exists = prev.recurringWeekDays.includes(key)
			return {
				...prev,
				recurringWeekDays: exists
					? prev.recurringWeekDays.filter((d) => d !== key)
					: [...prev.recurringWeekDays, key]
			}
		})
	}, [])

	const handleReminderChange = useCallback((val: number | null) => {
		setDraft((prev) => ({ ...prev, reminderOffsetHours: val }))
	}, [])

	const handleSave = useCallback(() => {
		if (draft.recurringPattern === 'NEVER' && !draft.deadlineAt) {
			setDueError(true)
			return
		}
		setDueError(false)
		if (draft.recurringPattern === 'WEEKLY' && draft.recurringWeekDays.length === 0) {
			setWeekDayError(true)
			return
		}
		setWeekDayError(false)
		const computedDeadline = (
			draft.recurringPattern !== 'NEVER'
			&& draft.completionWindowHours
			&& draft.completionWindowHours > 0
			&& draft.deadlineAt
		)
			? draft.deadlineAt.clone().add(draft.completionWindowHours, 'hours')
			: draft.deadlineAt
		onChange?.({ ...draft, deadlineAt: computedDeadline })
		setOpen(false)
	}, [draft, onChange])

	const handleRemove = useCallback(() => {
		setOpen(false)
		onRemove?.()
	}, [onRemove])

	const handleStartAtInput = useCallback((parsed: moment.Moment) => {
		setDraft((prev) => ({ ...prev, startAt: parsed.startOf('day') }))
		setCalendarCurrent(parsed.clone().startOf('month'))
	}, [])

	const handleDeadlineAtInput = useCallback((parsed: moment.Moment) => {
		setDraft((prev) => {
			if (!prev.deadlineAt) return { ...prev, deadlineAt: parsed.hour(12).minute(0).second(0) }
			return {
				...prev,
				deadlineAt: prev.deadlineAt.clone().year(parsed.year()).month(parsed.month()).date(parsed.date())
			}
		})
		setCalendarCurrent(parsed.clone().startOf('month'))
	}, [])

	const handleVisibleChange = useCallback((v: boolean) => {
		if (!v) {
			setDueError(false)
			setWeekDayError(false)
			onDismiss?.()
		}
		setOpen(v)
	}, [onDismiss])

	const recurringOptions = [
		{ value: 'NEVER', label: 'Nikdy' },
		{ value: 'DAILY', label: 'Denne' },
		{ value: 'WEEKDAYS', label: 'Pondelok až piatok' },
		{ value: 'WEEKLY', label: 'Týždenne' },
		...(dueEnabled && draft.deadlineAt !== null
			? [
				{
					value: 'MONTHLY_DAY',
					label: `Mesačne ${draft.deadlineAt.date()}.${draft.deadlineAt.format('MM')}.`
				}
			]
			: [])
	]

	const calendarHeaderRender = () => {
		const monthLabel = calendarCurrent.format('MMMM YYYY')
		return (
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					padding: '6px 4px 2px'
				}}
			>
				<button
					type={'button'}
					onClick={() => setCalendarCurrent((p) => p.clone().subtract(1, 'year'))}
					style={{
						background: 'none',
						border: 'none',
						cursor: 'pointer',
						color: '#6b7280',
						fontSize: 13,
						padding: '2px 4px'
					}}
				>
					«
				</button>
				<button
					type={'button'}
					onClick={() => setCalendarCurrent((p) => p.clone().subtract(1, 'month'))}
					style={{
						background: 'none',
						border: 'none',
						cursor: 'pointer',
						color: '#6b7280',
						fontSize: 13,
						padding: '2px 4px'
					}}
				>
					‹
				</button>
				<span
					style={{
						fontWeight: 600,
						fontSize: 14,
						color: '#111827',
						flex: 1,
						textAlign: 'center'
					}}
				>
					{monthLabel}
				</span>
				<button
					type={'button'}
					onClick={() => setCalendarCurrent((p) => p.clone().add(1, 'month'))}
					style={{
						background: 'none',
						border: 'none',
						cursor: 'pointer',
						color: '#6b7280',
						fontSize: 13,
						padding: '2px 4px'
					}}
				>
					›
				</button>
				<button
					type={'button'}
					onClick={() => setCalendarCurrent((p) => p.clone().add(1, 'year'))}
					style={{
						background: 'none',
						border: 'none',
						cursor: 'pointer',
						color: '#6b7280',
						fontSize: 13,
						padding: '2px 4px'
					}}
				>
					»
				</button>
			</div>
		)
	}

	const dateFullCellRender = (date: moment.Moment) => {
		const isStart = startEnabled && draft.startAt !== null && date.isSame(draft.startAt, 'day')
		const isDue = dueEnabled && draft.deadlineAt !== null && date.isSame(draft.deadlineAt, 'day')
		const hasRange = startEnabled
			&& dueEnabled
			&& draft.startAt !== null
			&& draft.deadlineAt !== null
			&& draft.deadlineAt.isAfter(draft.startAt, 'day')
		const isInRange = !isStart
			&& !isDue
			&& hasRange
			&& draft.startAt !== null
			&& draft.deadlineAt !== null
			&& date.isAfter(draft.startAt, 'day')
			&& date.isBefore(draft.deadlineAt, 'day')

		// Band background: full strip for in-range, half-gradient for endpoints
		let bandBg = 'transparent'
		if (isInRange) {
			bandBg = '#dbeafe'
		} else if (isStart && hasRange) {
			bandBg = 'linear-gradient(90deg, transparent 50%, #dbeafe 50%)'
		} else if (isDue && hasRange) {
			bandBg = 'linear-gradient(90deg, #dbeafe 50%, transparent 50%)'
		}

		const isEndpoint = isStart || isDue
		let dotColor = '#111827'
		if (isEndpoint) {
			dotColor = '#fff'
		} else if (isInRange) {
			dotColor = '#1e40af'
		}

		return (
			<div
				style={{
					width: '100%',
					height: 32,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					background: bandBg,
					margin: '0 -2px',
					padding: '0 2px'
				}}
			>
				<div
					style={{
						width: 28,
						height: 28,
						borderRadius: isEndpoint ? '50%' : 0,
						background: isEndpoint ? '#1d4ed8' : 'transparent',
						color: dotColor,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						fontSize: 13,
						fontWeight: isEndpoint ? 600 : 400,
						flexShrink: 0
					}}
				>
					{date.date()}
				</div>
			</div>
		)
	}

	const popoverContent = (
		<div style={{ width: 320 }}>
			{/* Header */}
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					padding: '10px 14px 6px',
					borderBottom: '1px solid #e5e7eb'
				}}
			>
				<span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>Dátumy</span>
				<button
					type={'button'}
					onClick={() => setOpen(false)}
					style={{
						background: 'none',
						border: 'none',
						fontSize: 20,
						color: '#9ca3af',
						cursor: 'pointer',
						lineHeight: 1,
						padding: '0 2px'
					}}
				>
					×
				</button>
			</div>

			{/* Calendar */}
			<div style={{ padding: '0 8px' }}>
				<Calendar
					fullscreen={false}
					value={calendarCurrent}
					onSelect={handleCalendarSelect}
					dateFullCellRender={dateFullCellRender}
					headerRender={calendarHeaderRender}
				/>
			</div>

			{/* Start date row */}
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					gap: 8,
					padding: '6px 14px'
				}}
			>
				<Checkbox
					checked={startEnabled}
					onChange={(e) => handleStartAtCheck(e.target.checked)}
				/>
				<span style={{ fontSize: 13, color: '#374151', minWidth: 100 }}>Dátum začiatku</span>
				{draft.startAt && (
					<DateInput value={draft.startAt} onCommit={handleStartAtInput} />
				)}
			</div>

			{/* Due date row — checkbox mode only for NEVER */}
			{draft.recurringPattern === 'NEVER' && (
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 8,
						padding: '6px 14px',
						flexWrap: 'wrap'
					}}
				>
					<Checkbox
						checked={dueEnabled}
						onChange={(e) => {
							setDueError(false)
							handleDeadlineCheck(e.target.checked)
						}}
					/>
					<span style={{ fontSize: 13, color: dueError ? '#ef4444' : '#374151', minWidth: 100 }}>Dátum splnenia</span>
					{draft.deadlineAt && (
						<>
							<DateInput value={draft.deadlineAt} onCommit={handleDeadlineAtInput} />
							<TimePicker
								use12Hours={false}
								format={'HH:mm'}
								value={draft.deadlineAt}
								onChange={handleTimeChange}
								size={'small'}
								allowClear={false}
							/>
						</>
					)}
					{dueError && (
						<span style={{ width: '100%', fontSize: 12, color: '#ef4444', paddingLeft: 28 }}>
							Dátum splnenia je povinný
						</span>
					)}
				</div>
			)}

			{/* Recurring section */}
			<div ref={recurrenceSectionRef} style={{ padding: '8px 14px' }}>
				<div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Opakovanie</div>
				{linkedTemplate ? (
					<div
						style={{
							border: '1px solid #e5e7eb',
							borderRadius: 8,
							padding: '8px 12px',
							background: '#f9fafb',
							fontSize: 13
						}}
					>
						<div style={{ color: '#6b7280', fontSize: 11, marginBottom: 4 }}>Zo šablóny</div>
						<div style={{ fontWeight: 600, color: '#111827', marginBottom: 2 }}>{linkedTemplate.title}</div>
						<div style={{ color: '#6b7280', fontSize: 12, marginBottom: linkedTemplate.completionWindowHours ? 4 : 8 }}>
							{(() => {
								const DAY: Record<string, string> = { SUN: 'Ne', MON: 'Po', TUE: 'Ut', WED: 'St', THU: 'Št', FRI: 'Pi', SAT: 'So' }
								if (linkedTemplate.frequency === 'WEEKLY') {
									return linkedTemplate.weekDays?.length
										? linkedTemplate.weekDays.map((d) => DAY[d] || d).join(', ')
										: 'Týždenne'
								}
								if (linkedTemplate.frequency === 'MONTHLY') return 'Mesačne'
								if (linkedTemplate.workingDaysOnly) return 'Pondelok až piatok'
								return 'Denne'
							})()}
						</div>
						{linkedTemplate.completionWindowHours != null && (
							<div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
								<span style={{ fontSize: 11, color: '#9ca3af' }}>Čas na splnenie:</span>
								<span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{linkedTemplate.completionWindowHours} h</span>
							</div>
						)}
						{onOpenTemplate && (
							<span
								onClick={(e) => { e.stopPropagation(); onOpenTemplate() }}
								style={{
									color: '#337ab7',
									fontSize: 12,
									cursor: 'pointer',
									textDecoration: 'underline'
								}}
							>
								Detail šablóny →
							</span>
						)}
					</div>
				) : (
					<Select
						value={draft.recurringPattern}
						onChange={handleRecurringPattern}
						style={{ width: '100%' }}
						size={'small'}
					>
						{recurringOptions.map((opt) => (
							<Select.Option key={opt.value} value={opt.value}>
								{opt.label}
							</Select.Option>
						))}
					</Select>
				)}

				{draft.recurringPattern === 'WEEKLY' && (
					<div>
						<div
							style={{
								display: 'flex',
								gap: 4,
								marginTop: 8,
								padding: weekDayError ? '6px 8px' : undefined,
								borderRadius: weekDayError ? 6 : undefined,
								border: weekDayError ? '1px solid #ef4444' : undefined,
								background: weekDayError ? '#fef2f2' : undefined
							}}
						>
							{WEEK_DAYS.map((label, idx) => {
								const key = DAY_KEYS[idx]
								const active = draft.recurringWeekDays.includes(key)
								return (
									<button
										key={key + String(idx)}
										type={'button'}
										onClick={() => handleToggleWeekDay(key)}
										style={{
											width: 32,
											height: 32,
											borderRadius: '50%',
											border: 'none',
											cursor: 'pointer',
											fontSize: 12,
											fontWeight: 600,
											background: active ? '#1d4ed8' : '#f3f4f6',
											color: active ? '#ffffff' : '#374151'
										}}
									>
										{label}
									</button>
								)
							})}
						</div>
						{weekDayError && (
							<div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>
								Vyber aspoň jeden deň opakovania.
							</div>
						)}
					</div>
				)}

				{/* Čas na splnenie — shown only when recurring */}
				{draft.recurringPattern !== 'NEVER' && (
					<>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: 8,
								marginTop: 10
							}}
						>
							<span style={{ fontSize: 13, color: '#374151', minWidth: 100 }}>Čas na splnenie</span>
							<input
								type={'number'}
								min={0}
								value={draft.completionWindowHours ?? ''}
								placeholder={'0'}
								onChange={(e) => {
									const raw = e.target.value
									const parsed = raw === '' ? null : parseInt(raw, 10)
									setDraft((prev) => ({ ...prev, completionWindowHours: parsed !== null && !Number.isNaN(parsed) ? parsed : null }))
								}}
								style={{
									border: '1px solid #d1d5db',
									borderRadius: 6,
									padding: '3px 8px',
									fontSize: 13,
									width: 72,
									outline: 'none',
									color: '#374151',
									fontFamily: 'inherit'
								}}
							/>
							<span style={{ fontSize: 13, color: '#374151' }}>hodín</span>
						</div>
						<div style={{ marginTop: 4 }}>
							<span style={{ fontSize: 12, color: '#6b7280' }}>
								Dátum splnenia: <strong style={{ color: '#374151' }}>{deriveDeadlineLabel(draft.deadlineAt, draft.completionWindowHours)}</strong>
							</span>
						</div>
						<div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
							<Checkbox
								checked={draft.fixedCompletion ?? false}
								onChange={(e) => setDraft((prev) => ({ ...prev, fixedCompletion: e.target.checked }))}
							/>
							<span style={{ fontSize: 13, color: '#374151' }}>Úloha s pevným dokončením</span>
						</div>
					</>
				)}
			</div>

			{/* Footer */}
			<div
				style={{
					display: 'flex',
					gap: 8,
					padding: '10px 14px',
					borderTop: '1px solid #e5e7eb',
					justifyContent: 'flex-end'
				}}
			>
				<button
					type={'button'}
					onClick={handleRemove}
					style={{
						padding: '4px 14px',
						border: '1px solid #d1d5db',
						borderRadius: 6,
						background: '#fff',
						cursor: 'pointer',
						fontSize: 13,
						color: '#374151'
					}}
				>
					Odstrániť
				</button>
				<button
					type={'button'}
					onClick={handleSave}
					style={{
						padding: '4px 14px',
						border: 'none',
						borderRadius: 6,
						background: '#2563eb',
						cursor: 'pointer',
						fontSize: 13,
						color: '#fff',
						fontWeight: 600
					}}
				>
					Uložiť
				</button>
			</div>
		</div>
	)

	const v = value ?? DEFAULT_VALUE
	const hasDate = Boolean(v.deadlineAt || v.startAt || (v.recurringPattern && v.recurringPattern !== 'NEVER'))
	const isRecurring = v.recurringPattern && v.recurringPattern !== 'NEVER'

	const DAY_SK: Record<string, string> = {
		SUN: 'Ne', MON: 'Po', TUE: 'Ut', WED: 'St', THU: 'Št', FRI: 'Pi', SAT: 'So'
	}

	let dateText = ''
	if (v.startAt && v.deadlineAt) {
		dateText = `${v.startAt.format('D.M.')} – ${v.deadlineAt.format('D.M. HH:mm')}`
	} else if (v.deadlineAt) {
		dateText = v.deadlineAt.format('D.M. HH:mm')
	} else if (v.startAt) {
		dateText = v.startAt.format('D.M.')
	}

	let recurringLabel = ''
	if (isRecurring) {
		if (v.recurringPattern === 'DAILY') {
			recurringLabel = 'Denne'
		} else if (v.recurringPattern === 'WEEKDAYS') {
			recurringLabel = 'Po–Pi'
		} else if (v.recurringPattern === 'WEEKLY' && v.recurringWeekDays?.length) {
			const weekDays = Array.isArray(v.recurringWeekDays)
				? v.recurringWeekDays
				: (() => { try { const p = JSON.parse(v.recurringWeekDays as unknown as string); return Array.isArray(p) ? p : [] } catch { return [] } })()
			recurringLabel = (weekDays as string[]).map((d) => DAY_SK[d] || d).join(', ')
		} else if (v.recurringPattern === 'MONTHLY_DAY' && v.deadlineAt) {
			recurringLabel = `${v.deadlineAt.date()}.${v.deadlineAt.format('MM')}.`
		}
	}

	const handleRecurrencePillClick = (event: React.MouseEvent<HTMLSpanElement> | React.KeyboardEvent<HTMLSpanElement>) => {
		event.stopPropagation()
		if (recurrencePill?.readOnly) return
		if (recurrencePill?.inactive) {
			// inactive template: call onClick (shows reactivation dialog) but don't open the date popover
			recurrencePill?.onClick?.()
			return
		}
		setOpen(true)
		recurrencePill?.onClick?.()
	}

	const recurrencePillNode = recurrencePill ? (
		<span
			role={recurrencePill.readOnly ? undefined : 'button'}
			tabIndex={recurrencePill.readOnly ? undefined : 0}
			// eslint-disable-next-line no-nested-ternary
			aria-label={recurrencePill.readOnly ? 'Opakovanie šablóny' : recurrencePill.inactive ? 'Aktivovať opakovanie šablóny' : 'Upraviť opakovanie šablóny'}
			onClick={handleRecurrencePillClick}
			onKeyDown={(event) => {
				if (recurrencePill.readOnly) return
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault()
					handleRecurrencePillClick(event)
				}
			}}
			style={{
				display: 'inline-flex',
				alignItems: 'center',
				gap: 4,
				color: recurrencePill.inactive ? '#9ca3af' : '#337ab7',
				fontSize: 13,
				fontWeight: 600,
				cursor: recurrencePill.readOnly ? 'default' : 'pointer',
				lineHeight: 1.4
			}}
		>
			{recurrencePill.label}
		</span>
	) : null

	const trigger = hasDate ? (
		<div
			style={{
				display: 'inline-flex',
				alignItems: 'center',
				gap: 6,
				padding: '3px 10px',
				border: '1px solid #d1d5db',
				borderRadius: 20,
				background: '#fff',
				cursor: 'pointer',
				fontSize: 13,
				color: '#374151',
				userSelect: 'none'
			}}
		>
			<span>{dateText}</span>
			{v.fixedCompletion && (
				<Tooltip title={'Úloha s pevným dokončením'}>
					<FieldTimeOutlined style={{ color: '#fa8c16', fontSize: 13 }} />
				</Tooltip>
			)}
			{recurrencePillNode}
			{!recurrencePill && isRecurring && (
				<span
					style={{
						display: 'inline-flex',
						alignItems: 'center',
						gap: 3,
						color: '#6b7280',
						fontSize: 12
					}}
				>
					<span>↻</span>
					{recurringLabel && <span>{recurringLabel}</span>}
				</span>
			)}
		</div>
	) : (
		<span style={{ color: '#9ca3af', fontSize: 13, cursor: 'pointer' }}>(+ nastaviť dátum)</span>
	)

	return (
		<Popover
			visible={open}
			onVisibleChange={handleVisibleChange}
			content={popoverContent}
			trigger={'click'}
			placement={'bottomLeft'}
			overlayInnerStyle={{ padding: 0 }}
			getPopupContainer={(trigger) => (trigger.closest('.ant-modal-body') as HTMLElement) ?? document.body}
		>
			{trigger}
		</Popover>
	)
}

export default DatePickerPopover
